/**
 * SyncScheduleService.js
 * 
 * Service for managing sync schedule operations.
 */
import axios from 'axios';
import { getApiBaseUrl, getAuthHeaders } from '../utils/urlConfig';

class SyncScheduleService {
  constructor() {
    this.apiUrl = `${getApiBaseUrl()}/admin/sync-schedule`;
  }

  /**
   * Get all sync schedules
   * @returns {Promise} - Resolves with schedules data
   */
  async getSchedules() {
    try {
      const response = await axios.get(this.apiUrl, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching sync schedules:', error);
      throw error;
    }
  }

  /**
   * Get all sync schedules with last sync information
   * @param {boolean} includeLastSync - Whether to include last sync information
   * @returns {Promise} - Resolves with schedules data including last sync info
   */
  async getSchedulesWithLastSync(includeLastSync = true) {
    try {
      const response = await axios.get(`${this.apiUrl}?includeLastSync=${includeLastSync}`, {
        headers: getAuthHeaders(),
        timeout: 15000
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching sync schedules with last sync info:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive sync overview including schedules and last sync information
   * @returns {Promise} - Resolves with comprehensive sync overview data
   */
  async getSyncOverview() {
    try {
      const response = await axios.get(`${this.apiUrl}/overview`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching sync overview:', error);
      throw error;
    }
  }

  /**
   * Get schedule for specific data type
   * @param {string} dataType - Type of data
   * @returns {Promise} - Resolves with schedule data
   */
  async getScheduleByType(dataType) {
    try {
      const response = await axios.get(`${this.apiUrl}?dataType=${dataType}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching sync schedule for ${dataType}:`, error);
      throw error;
    }
  }

  /**
   * Create or update a sync schedule
   * @param {Object} scheduleData - Schedule configuration
   * @returns {Promise} - Resolves with created/updated schedule data
   */
  async saveSchedule(scheduleData) {
    try {
      const response = await axios.post(this.apiUrl, scheduleData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error saving sync schedule:', error);
      throw error;
    }
  }

  /**
   * Update an existing sync schedule
   * @param {number} id - Schedule ID
   * @param {Object} scheduleData - Updated schedule configuration
   * @returns {Promise} - Resolves with updated schedule data
   */
  async updateSchedule(id, scheduleData) {
    try {
      const response = await axios.put(`${this.apiUrl}/${id}`, scheduleData, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error updating sync schedule:', error);
      throw error;
    }
  }

  /**
   * Delete a sync schedule
   * @param {number} id - Schedule ID
   * @returns {Promise} - Resolves with success message
   */
  async deleteSchedule(id) {
    try {
      const response = await axios.delete(`${this.apiUrl}/${id}`, {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting sync schedule:', error);
      throw error;
    }
  }
}

const syncScheduleService = new SyncScheduleService();
export default syncScheduleService; 